import React, { useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  Counter,
  Documentation,
  Intervention,
  AccessLevelEnum,
  Nomenclature,
} from 'graphql-common';
import { SubmitAction } from '@lib/enums/form';
import {
  UseBulkSelectResult,
  useTableItemsBulkSelect,
} from '@lib/hooks/useTableItemsBulkSelect';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { FormRef } from '@lib/components/ReactHookForm/types';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TabCountersContext from '@lib/contexts/TabCountersContext';
import useTabCounters from '@lib/hooks/useTabCounters';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import AssetMenuBar from 'components/AssetMenuBar/AssetMenuBar';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import AssetNomenclatureContainer from './AssetNomenclatureContainer';
import AssetCountersContainer from './AssetCountersContainer';
import AssetDetailsContainer from './AssetDetailsContainer';
import AssetDocumentationsContainer from './AssetDocumentationsContainer';
import AssetInterventionsContainer from './AssetInterventionsContainer';
import useSearchListQueries from './utils/useSearchListQueries';
import useModalManagement from './utils/useModalManagement';
import useActions from './utils/useActions';
import { AssetViewMode } from './enums';

type Props = {
  viewMode: AssetViewMode;
};

export default function AssetContainer({ viewMode }: Props) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const submitNewItemRef = useRef<SubmitAction>(undefined);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) ===
    AccessLevelEnum.Read;
  const isCounterViewOnly =
    getUserAccessByComponent(AccessComponentEnum.AssetsCounterReading) ===
    AccessLevelEnum.LimitedRead;

  const useBulkSelectResult = useTableItemsBulkSelect<
    Nomenclature | Counter | Documentation | Intervention
  >();

  const useModalManagementResult = useModalManagement();

  const useActionsResult = useActions({
    id,
    viewMode,
  });
  const { assetQueryHook } = useActionsResult;

  // Tab counters come from the asset query, which only refires when
  // `includeFullData` flips (About ↔ the rest) — moving between nomenclature,
  // counters, documents and interventions leaves the variables untouched, so
  // the numbers would freeze. Refresh them on every tab switch, and expose the
  // refetch to tab containers whose mutations change a count. `loading` is
  // passed so the About ↔ tab hops, where Apollo already refetches on the
  // changed variables, don't fire a second identical request.
  const tabCounters = useTabCounters({
    loading: assetQueryHook.loading,
    refetch: assetQueryHook.refetch,
    skip: !id,
  });

  const useSearchListQueriesResult = useSearchListQueries();

  // Only the very first paint gets a full-page loader — there is no shell worth
  // framing yet. `firstLoading` alone can't be the gate: it means "no data for
  // *these* variables", and the `includeFullData` flip on an About ↔ tab hop
  // makes it true again on a cold cache, which would tear the whole page (tab
  // bar included) down on a tab click. `previousData` behind `data` is what
  // tells the two apart.
  if (assetQueryHook.firstLoading && !assetQueryHook.data) {
    return <CircleLoader />;
  }

  if (assetQueryHook.error) {
    return (
      <ErrorPage
        status={getStatusFromError(assetQueryHook.error)}
        description={getDescriptionFromError(assetQueryHook.error)}
      />
    );
  }

  return (
    <TabCountersContext.Provider value={tabCounters}>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: assetQueryHook.data || undefined,
        })}
      />
      <PageBody withoutHeader>
        <AssetMenuBar
          id={id}
          counters={assetQueryHook.counters}
          onChange={useBulkSelectResult.handleUnselectAllTableItems}
        />
        {assetQueryHook.firstLoading ? (
          <CircleLoader />
        ) : (
          <>
            {viewMode === AssetViewMode.View && (
              <AssetDetailsContainer
                formRef={formRef}
                useActionsResult={useActionsResult}
                useModalManagementResult={useModalManagementResult}
                useSearchListQueriesResult={useSearchListQueriesResult}
                viewMode={viewMode}
                isViewOnly={isViewOnly}
              />
            )}
            {viewMode === AssetViewMode.Nomenclatures && (
              <AssetNomenclatureContainer
                formRef={formRef}
                submitNewItemRef={submitNewItemRef}
                useActionsResult={useActionsResult}
                useBulkSelectResult={
                  useBulkSelectResult as UseBulkSelectResult<Nomenclature>
                }
                useModalManagementResult={useModalManagementResult}
                useSearchListQueriesResult={useSearchListQueriesResult}
                viewMode={viewMode}
                isViewOnly={isViewOnly}
              />
            )}
            {viewMode === AssetViewMode.Counters && (
              <AssetCountersContainer
                formRef={formRef}
                submitNewItemRef={submitNewItemRef}
                useActionsResult={useActionsResult}
                useBulkSelectResult={
                  useBulkSelectResult as UseBulkSelectResult<Counter>
                }
                useModalManagementResult={useModalManagementResult}
                useSearchListQueriesResult={useSearchListQueriesResult}
                viewMode={viewMode}
                isViewOnly={isViewOnly}
                isCounterViewOnly={isCounterViewOnly}
              />
            )}
            {viewMode === AssetViewMode.Documentation && (
              <AssetDocumentationsContainer
                id={id}
                useActionsResult={useActionsResult}
                useBulkSelectResult={
                  useBulkSelectResult as UseBulkSelectResult<Documentation>
                }
                isViewOnly={isViewOnly}
              />
            )}
            {viewMode === AssetViewMode.Interventions && (
              <AssetInterventionsContainer
                id={id}
                useActionsResult={useActionsResult}
                useBulkSelectResult={
                  useBulkSelectResult as UseBulkSelectResult<Intervention>
                }
                viewMode={viewMode}
              />
            )}
          </>
        )}
      </PageBody>
    </TabCountersContext.Provider>
  );
}
